/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useMemo } from 'react'

import {
  SignatureState,
  useCheckOperation,
  User,
} from '@builddocs/data-management'
import {
  DocumentSigningTask,
  updateStamp,
  Application,
  ApplicationKeys,
  useStamp,
} from '@builddocs/filestore-api'
import { Stamp } from '@builddocs/mui'
import { useToast } from '@builddocs/system'
import { Popover } from '@mui/material'
import { Button, Flex, Tooltip, Skeleton } from 'antd'
import { AxiosError } from 'axios'
import {
  usePopupState,
  bindToggle,
  bindPopover,
} from 'material-ui-popup-state/hooks'

import StampViewer, { getTitleStamp } from './StampViewer'

export interface StatusStampProps {
  signingTask: DocumentSigningTask
  docId: string
  documentType: ApplicationKeys
  user: User
  refresh?: () => void
  onUpdatePDF?: () => void
}

const RefreshIcon = ({ disabled }: { disabled?: boolean }) => (
  <svg
    fill="none"
    height="16"
    style={{
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_19928_3103)">
      <path
        clipRule="evenodd"
        d="M3.40381 3.40381C4.57951 2.22808 6.2052 1.5 7.99999 1.5C11.5898 1.5 14.5 4.41016 14.5 8C14.5 11.5898 11.5898 14.5 7.99999 14.5C6.2052 14.5 4.57951 13.7719 3.4038 12.5962C3.20854 12.4009 3.20855 12.0843 3.40381 11.8891C3.59908 11.6938 3.91566 11.6938 4.11092 11.8891C5.10675 12.8849 6.48106 13.5 7.99999 13.5C11.0375 13.5 13.5 11.0376 13.5 8C13.5 4.96244 11.0375 2.5 7.99999 2.5C6.48106 2.5 5.10675 3.11506 4.11092 4.11092C3.8445 4.37733 3.41303 4.84584 3.04179 5.25517C2.85758 5.45828 2.69048 5.64428 2.56941 5.77953C2.5089 5.84714 2.45992 5.90202 2.42612 5.93995L2.37405 5.99845L1.99999 5.66667L1.62615 5.33465L1.67955 5.27464C1.71379 5.23622 1.76325 5.18079 1.82431 5.11258C1.9464 4.97619 2.11501 4.7885 2.30106 4.58336C2.67033 4.17621 3.11755 3.69007 3.40381 3.40381C3.40381 3.40381 3.40381 3.40381 3.40381 3.40381ZM1.668 6.04054C1.46151 5.85718 1.44279 5.54113 1.62615 5.33465L1.99999 5.66667L2.37405 5.99845C2.19069 6.20494 1.87448 6.22389 1.668 6.04054Z"
        fill="#1C1B1E"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="M2 2.5C2.27614 2.5 2.5 2.72386 2.5 3V5.16667H4.66667C4.94281 5.16667 5.16667 5.39052 5.16667 5.66667C5.16667 5.94281 4.94281 6.16667 4.66667 6.16667H2C1.72386 6.16667 1.5 5.94281 1.5 5.66667V3C1.5 2.72386 1.72386 2.5 2 2.5Z"
        fill="#1C1B1E"
        fillRule="evenodd"
      />
    </g>
    <defs>
      <clipPath id="clip0_19928_3103">
        <rect fill="white" height="16" width="16" />
      </clipPath>
    </defs>
  </svg>
)

const AccessTimeRoundedIcon = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_19943_3098)">
      <circle cx="8" cy="8" fill="#FFC554" r="8" />
      <path
        d="M7.78 3H7.72C7.32 3 7 3.32 7 3.72V8.44C7 8.79 7.18 9.12 7.49 9.3L11.64 11.79C11.98 11.99 12.42 11.89 12.62 11.55C12.83 11.21 12.72 10.76 12.37 10.56L8.5 8.26V3.72C8.5 3.32 8.18 3 7.78 3Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_19943_3098">
        <rect fill="white" height="16" width="16" />
      </clipPath>
    </defs>
  </svg>
)

const DoneRoundedIcon = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_19943_3093)">
      <circle cx="8" cy="8" fill="#43A047" r="8" />
      <path d="M3 8L6.5 11.5L13 5" stroke="white" strokeWidth="1.5" />
    </g>
    <defs>
      <clipPath id="clip0_19943_3093">
        <rect fill="white" height="16" width="16" />
      </clipPath>
    </defs>
  </svg>
)

const CloseRoundedIcon = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_19943_2122)">
      <circle cx="8" cy="8" fill="#F20404" r="8" />
      <path d="M11 5L5 11M5 5L11 11" stroke="white" />
    </g>
    <defs>
      <clipPath id="clip0_19943_2122">
        <rect fill="white" height="16" width="16" />
      </clipPath>
    </defs>
  </svg>
)

const PriorityHighRoundedIcon = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 3 16"
    width="3"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 2.5C0 1.67157 0.671573 1 1.5 1C2.32843 1 3 1.67157 3 2.5V9.5C3 10.3284 2.32843 11 1.5 11C0.671573 11 0 10.3284 0 9.5V2.5Z"
      fill="#ED6C02"
    />
    <path
      d="M0 13.5C0 12.6716 0.671573 12 1.5 12C2.32843 12 3 12.6716 3 13.5C3 14.3284 2.32843 15 1.5 15C0.671573 15 0 14.3284 0 13.5Z"
      fill="#ED6C02"
    />
  </svg>
)

export function StatusStamp({
  signingTask,
  docId,
  documentType,
  user,
  refresh,
  onUpdatePDF,
}: StatusStampProps) {
  const { data: stampById, isLoading } = useStamp({
    stampId: signingTask.stampTemplateId,
  })

  const { toast } = useToast()

  const isStampCreator =
    !signingTask.creatorId || signingTask.creatorId === user.userId

  const userSigner = signingTask.stampSigns?.find(
    signer =>
      signer.userId === user?.userId && signer.signatureState === 'PENDING',
  )

  const statusStamp: 'REJECTED' | 'PENDING' | 'ACCEPTED' = useMemo(
    () =>
      signingTask.stampSigns?.some(
        signer => signer.signatureState === 'REJECTED',
      )
        ? 'REJECTED'
        : signingTask.stampSigns?.some(
            _signer => _signer.signatureState === 'PENDING',
          )
        ? 'PENDING'
        : 'ACCEPTED',
    [signingTask.stampSigns],
  )

  const handleUpdateStamp = () => {
    if (window.confirm('Отправить повторно на подпись?')) {
      updateStamp({
        ...signingTask,
        stampSigns: signingTask.stampSigns.map(signer => ({
          ...signer,
          signatureState: SignatureState.PENDING,
        })),
      })
        .then(() => {
          onUpdatePDF && onUpdatePDF()
          refresh && refresh()
          toast.success('Штамп обновлён')
        })
        .catch(error => {
          const e = error as AxiosError

          toast.warning(
            e?.response?.data?.message ??
              'Не удалось повторно отправить на подпись',
          )
        })
    }
  }

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'adviceBubble',
  })

  const { checkOperation } = useCheckOperation()

  if (isLoading) {
    return <Skeleton.Input active size="small" style={{ height: '28px' }} />
  }

  const handleUpdateStampClick = () => {
    checkOperation({
      operationId: 'STAMP_RESEND',
      operationCallback: handleUpdateStamp,
    })
  }

  return (
    <Flex align="center" gap={4} style={{ height: '28px' }} vertical={false}>
      <Flex vertical align="center" justify="center">
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ width: '114px' }}
          {...bindToggle(popupState)}
        >
          {stampById && (
            <Stamp
              picture={`${stampById?.pngFileByte}`}
              title={getTitleStamp(stampById?.name)}
            />
          )}
        </Flex>
        <Popover
          {...bindPopover(popupState)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {stampById && (
            <StampViewer
              docId={docId}
              documentType={documentType}
              signingTask={signingTask}
              stamp={stampById}
              statusStamp={statusStamp}
              user={user}
              userSigner={userSigner}
              onUpdatePDF={onUpdatePDF}
            />
          )}
        </Popover>
      </Flex>
      <Flex align="center" gap={8} justify="end" vertical={false}>
        {statusStamp === 'REJECTED' && <CloseRoundedIcon />}
        {statusStamp === 'PENDING' && <AccessTimeRoundedIcon />}
        {statusStamp === 'ACCEPTED' && <DoneRoundedIcon />}
        {userSigner && (
          <Tooltip arrow={false} title="Необходимо ваше действие">
            <Flex
              align="center"
              justify="center"
              style={{ height: '16px', cursor: 'help' }}
            >
              <PriorityHighRoundedIcon />
            </Flex>
          </Tooltip>
        )}
      </Flex>
      {refresh &&
        isStampCreator &&
        documentType !== Application.EXECUTIVE_DRAWING &&
        statusStamp === SignatureState.REJECTED && (
          <Button
            icon={<RefreshIcon />}
            size="small"
            styles={{
              icon: {
                width: '16px',
                height: '16px',
              },
            }}
            type="text"
            onClick={handleUpdateStampClick}
          />
        )}
    </Flex>
  )
}

export default StatusStamp
